import _ from "lodash";
import { ReactElement } from "react";

import {
  Checkbox,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  SxProps,
  Theme,
  useTheme,
} from "@mui/material";

interface IMultiSelectProperties<T> {
  clearIcon?: ReactElement;
  customRenderValue?: (selected: string[]) => string;
  defaultSelectedOptions: T[];
  formControlSx?: SxProps<Theme>;
  getOptionLabel: (option: T) => string;
  getOptionValue?: (option: T) => string;
  inputLabel?: string;
  isMultiSelect?: boolean;
  margin?: "dense" | "none" | "normal" | undefined;
  menuItemSx?: SxProps<Theme>;
  onChange: (value: string[]) => void;
  options: T[];
  selectedValues: string[];
  sx?: SxProps<Theme>;
}

/**
 * A reusable multi-select component with checkbox options and optional clear functionality.
 *
 * defaultSelectedOptions and options MUST BE the same type.
 *
 * @typeParam T - Type of the option objects in the select menu. Automatically inferred.
 *
 * @param clearIcon - Renders a "clear" button inside the input. If not passed in, input is not "clearable"
 * @param customRenderValue - Optional custom function to format how selected values are displayed when dropdown is closed
 * @param defaultSelectedOptions - Array of values to reset to when clear button is clicked. Usually either empty array [] or options
 * @param formControlSx - Optional styles for the FormControl component
 * @param getOptionLabel - Function to get the display label from an option
 * @param getOptionValue - Optional function to get the value from an option. Falls back to String(option) if not provided
 * @param inputLabel - Optional label text for the input
 * @param isMultiSelect - Whether the select is a multi-select or not
 * @param margin - Optional margin for the FormControl component
 * @param menuItemSx - Optional styles for the MenuItem components
 * @param onChange - Callback function when selection changes. Takes an array of option values generate by getOptionValue Prop
 * @param options - Array of options to display in the select menu. An array of ANY type of object.
 * @param sx - Optional styles for the Select component
 * @param selectedValues - Currently selected values
 * @returns A React component that renders a multi-select dropdown with checkboxes
 *
 * @example
 * ```tsx
 * <CustomSelect
 *   options={users}
 *   selectedValues={selectedUserIds}
 *   onChange={handleUserSelection}
 *   getOptionLabel={(user) => `${user.firstName} ${user.lastName}`}
 *   getOptionValue={(user) => user.id}
 *   clearIcon={<ClearIcon />}
 *   defaultSelectedOptions={[]}
 * />
 * ```
 */
function CustomSelect<T>({
  clearIcon,
  customRenderValue = (selected) => selected.join(", "),
  defaultSelectedOptions,
  formControlSx,
  getOptionLabel,
  getOptionValue,
  inputLabel,
  isMultiSelect = true,
  margin = "dense",
  menuItemSx,
  onChange,
  options,
  selectedValues,
  sx,
}: IMultiSelectProperties<T>) {
  const theme = useTheme();

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const newValues = typeof event.target.value === "string" ? event.target.value.split(",") : event.target.value;
    onChange(newValues);
  };

  const getLabel = (option: T): string => {
    if (getOptionLabel) {
      return getOptionLabel(option);
    }
    return String(option);
  };

  const getValue = (option: T): string => {
    if (getOptionValue) {
      return getOptionValue(option);
    }
    return String(option);
  };

  const defaultValues = defaultSelectedOptions.map((option) => getValue(option));

  const resetSelection = () => {
    onChange(defaultValues);
  };

  const isCurrentValueDefault = !_.isEqual(
    _.sortBy(selectedValues),
    _.sortBy(defaultSelectedOptions.map((option) => getValue(option))),
  );

  const shouldShowReset = !!clearIcon && isCurrentValueDefault;

  return (
    <FormControl fullWidth margin={margin} size="small" sx={formControlSx}>
      {inputLabel && (
        <InputLabel id="multiple-checkbox-label" size="small" sx={{}} variant="outlined">
          {inputLabel}
        </InputLabel>
      )}
      <Select
        id="multiple-checkbox-select"
        input={
          <OutlinedInput
            endAdornment={
              shouldShowReset && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={"clear selections"}
                    edge="end"
                    onClick={resetSelection}
                    size="small"
                    style={{ marginRight: theme.spacing(1) }}
                    title="Reset selections"
                  >
                    {clearIcon}
                  </IconButton>
                </InputAdornment>
              )
            }
            label={inputLabel}
          />
        }
        labelId="multiple-checkbox-label"
        multiple={isMultiSelect}
        onChange={handleChange}
        renderValue={customRenderValue}
        sx={{
          ...sx,
          backgroundColor: theme.palette.common.white,
        }}
        value={selectedValues}
      >
        {options.map((option) => (
          <MenuItem key={getValue(option)} sx={menuItemSx} value={getValue(option)}>
            <Checkbox checked={selectedValues.includes(getValue(option))} />
            <ListItemText primary={getLabel(option)} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default CustomSelect;
